import React, { useState, useEffect } from 'react';
import { DreamForm } from './components/DreamForm';
import { ResultsView } from './components/ResultsView';
import { LoadingView } from './components/LoadingView';
import { DevModePanel } from './components/DevModePanel';
import { HamburgerMenu } from './components/HamburgerMenu';
import { AboutView } from './components/AboutView';
import { DreamHistoryView } from './components/DreamHistoryView';
import { Footer } from './components/Footer';
import { KofiButton } from './components/KofiButton';
import { useDreamAnalysis } from './hooks/useDreamAnalysis';
import { useDreamHistory } from './hooks/useDreamHistory';
import { dreamStorage } from './services/dream-storage';
import type { DreamFormData, AnalysisResults } from './types/frontend';

type ViewType = 'form' | 'loading' | 'results' | 'about' | 'history';

function App() {
  const [currentView, setCurrentView] = useState<ViewType>('form');
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [isDevMode, setIsDevMode] = useState(false);
  const [dreamCount, setDreamCount] = useState(0);

  const { analyzeDream, isAnalyzing } = useDreamAnalysis();
  const { dreams, isLoading: dreamsLoading, reloadDreams, deleteDream } = useDreamHistory();

  // Check for dev mode in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsDevMode(urlParams.get('dev') === 'true');
  }, []);

  // Update dream count
  useEffect(() => {
    const updateCount = async () => {
      const count = await dreamStorage.getDreamCount();
      setDreamCount(count);
    };
    updateCount();
  }, [dreams]);

  const handleSubmit = async (formData: DreamFormData) => {
    setCurrentView('loading');

    try {
      const results = await analyzeDream(formData);
      setAnalysisResults(results);

      // Save to IndexedDB
      await dreamStorage.saveDream(formData.dreamText, results);
      await reloadDreams();

      setCurrentView('results');
    } catch (error) {
      console.error('Dream analysis failed:', error);
      setCurrentView('form');
    }
  };

  const handleReset = () => {
    setCurrentView('form');
    setAnalysisResults(null);
  };

  const handleViewDream = (analysis: AnalysisResults) => {
    setAnalysisResults(analysis);
    setCurrentView('results');
  };

  const handleDeleteDream = async (id: string) => {
    if (confirm('Delete this dream?')) {
      await deleteDream(id);
    }
  };

  return (
    <div className="app">
      <header className="header">
        <div>
          <h1>
            <span
              onClick={handleReset}
              style={{
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.opacity = '0.7';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              Šāʾilu
            </span>
            <span
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 300,
                color: '#B8B3AA',
                marginLeft: 'var(--space-3)',
                marginRight: 'var(--space-3)',
              }}
            >
              |
            </span>
            <span
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 400,
                color: '#B8B3AA',
              }}
            >
              Mesopotamian dream interpretation
            </span>
          </h1>
          {isDevMode && (
            <div
              data-testid="dev-mode-banner"
              style={{
                marginTop: 'var(--space-4)',
                background: 'var(--stone-light)',
                border: '1px solid var(--stone-dark)',
                borderRadius: '4px',
                padding: 'var(--space-3) var(--space-4)',
                fontSize: 'var(--text-xs)',
                fontWeight: 500,
                color: 'var(--soot)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Dev Mode: HITL Training
            </div>
          )}
        </div>
        {!isDevMode && (
          <HamburgerMenu
            onAboutClick={() => setCurrentView('about')}
            onHistoryClick={() => setCurrentView('history')}
            dreamCount={dreamCount}
          />
        )}
      </header>

      <main className="main-content">
        {isDevMode ? (
          <DevModePanel
            data-testid="hitl-panel"
            onAnalyze={handleSubmit}
            analysisResults={analysisResults}
            onReset={handleReset}
            isAnalyzing={isAnalyzing}
          />
        ) : (
          <>
            {currentView === 'form' && (
              <DreamForm onSubmit={handleSubmit} isLoading={isAnalyzing} />
            )}

            {currentView === 'loading' && <LoadingView />}

            {currentView === 'results' && analysisResults && (
              <ResultsView 
                results={analysisResults} 
                onReset={handleReset}
              />
            )}

            {currentView === 'about' && <AboutView onClose={() => setCurrentView('form')} />}

            {currentView === 'history' && (
              <DreamHistoryView
                dreams={dreams}
                isLoading={dreamsLoading}
                onClose={() => setCurrentView('form')}
                onViewDream={handleViewDream}
                onDeleteDream={handleDeleteDream}
              />
            )}
          </>
        )}
      </main>

      <Footer />
      <KofiButton />
    </div>
  );
}

export default App;
